import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Factory,
  TrendingUp,
  Gem,
  Globe,
  DollarSign,
  Banknote,
  TrainFront,
  ScrollText,
  Scale,
  Pickaxe,
  Building2,
  FileText,
  Upload,
  Archive,
  FileDown,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  type LucideIcon,
} from 'lucide-react';
import { categories } from '../../config/categories.config';
import { useAuth } from '../../auth/useAuth';
import { useUIStore } from '../../store';

/** Map of icon name strings to Lucide icon components. */
const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard,
  Factory,
  TrendingUp,
  Gem,
  Globe,
  DollarSign,
  Banknote,
  TrainFront,
  ScrollText,
  Scale,
  Pickaxe,
  Building2,
  FileText,
  Upload,
};

/**
 * Left navigation sidebar with StratMined branding.
 */
export const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const {
    isSidebarOpen,
    isSidebarCollapsed,
    setSidebarOpen,
    toggleSidebarCollapse,
  } = useUIStore();

  const isAdmin = user?.role === 'admin';

  const roleBadgeStyles = useMemo(() => {
    switch (user?.role) {
      case 'admin':
        return 'bg-[#C08B5C]/20 text-[#D4A574]';
      case 'internal':
        return 'bg-[#1B8A7A]/20 text-[#23A08D]';
      default:
        return 'bg-[#6B7280]/20 text-[#6B7280]';
    }
  }, [user?.role]);

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="fixed top-4 left-4 z-30 p-2 rounded-lg bg-[#1A2332] text-white lg:hidden shadow-lg"
        onClick={() => setSidebarOpen(!isSidebarOpen)}
        aria-label="Toggle menu"
      >
        <Menu size={20} />
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-30 h-full flex flex-col
          bg-[#1A2332] text-white
          transition-all duration-300 ease-in-out
          ${isSidebarCollapsed ? 'w-20' : 'w-64'}
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Logo Section */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-[#2C3A50]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0">
              <svg
                width="36"
                height="36"
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M32 8L4 24L32 40L60 24L32 8Z"
                  fill="#C08B5C"
                  fillOpacity="0.9"
                />
                <path
                  d="M4 32L32 48L60 32"
                  stroke="#C08B5C"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4 40L32 56L60 40"
                  stroke="#D4A574"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <text
                  x="32"
                  y="28"
                  textAnchor="middle"
                  fill="white"
                  fontFamily="'Cormorant Garamond', serif"
                  fontSize="14"
                  fontWeight="700"
                >
                  SM
                </text>
              </svg>
            </div>
            {!isSidebarCollapsed && (
              <div className="min-w-0">
                <div
                  className="text-base font-semibold text-white leading-tight truncate"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  StratMined
                </div>
                <div className="text-[9px] tracking-[0.2em] text-[#C08B5C] uppercase font-medium">
                  Intelligence
                </div>
              </div>
            )}
          </div>

          {/* Collapse toggle (desktop only) */}
          <button
            className="hidden lg:flex items-center justify-center w-7 h-7 rounded-md hover:bg-[#2C3A50] text-[#6B7280] hover:text-white transition-colors"
            onClick={toggleSidebarCollapse}
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {/* Category navigation */}
          <div className="space-y-0.5">
            {categories.map((cat) => {
              const Icon = iconMap[cat.icon] || LayoutDashboard;
              const path = cat.slug === 'dashboard' ? '/dashboard' : `/${cat.slug}`;

              return (
                <NavLink
                  key={cat.slug}
                  to={path}
                  onClick={() => {
                    if (window.innerWidth < 1024) {
                      setSidebarOpen(false);
                    }
                  }}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group
                    ${
                      isActive
                        ? 'bg-[#C08B5C] text-white shadow-md shadow-[#C08B5C]/20'
                        : 'text-[#9CA3AF] hover:bg-[#2C3A50] hover:text-white'
                    }
                    ${isSidebarCollapsed ? 'justify-center' : ''}`
                  }
                  title={isSidebarCollapsed ? cat.displayName : undefined}
                >
                  <Icon
                    size={18}
                    className="flex-shrink-0"
                  />
                  {!isSidebarCollapsed && (
                    <span className="truncate">{cat.displayName}</span>
                  )}
                </NavLink>
              );
            })}
          </div>

          {/* Divider */}
          <div className="my-4 mx-3 border-t border-[#2C3A50]" />

          {/* Additional navigation */}
          <div className="space-y-0.5">
            <NavLink
              to="/archive"
              onClick={() => {
                if (window.innerWidth < 1024) setSidebarOpen(false);
              }}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                ${
                  isActive
                    ? 'bg-[#C08B5C] text-white shadow-md shadow-[#C08B5C]/20'
                    : 'text-[#9CA3AF] hover:bg-[#2C3A50] hover:text-white'
                }
                ${isSidebarCollapsed ? 'justify-center' : ''}`
              }
              title={isSidebarCollapsed ? 'Archive' : undefined}
            >
              <Archive size={18} className="flex-shrink-0" />
              {!isSidebarCollapsed && <span>Archive</span>}
            </NavLink>

            <NavLink
              to="/weekly-reports"
              onClick={() => {
                if (window.innerWidth < 1024) setSidebarOpen(false);
              }}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                ${
                  isActive
                    ? 'bg-[#C08B5C] text-white shadow-md shadow-[#C08B5C]/20'
                    : 'text-[#9CA3AF] hover:bg-[#2C3A50] hover:text-white'
                }
                ${isSidebarCollapsed ? 'justify-center' : ''}`
              }
              title={isSidebarCollapsed ? 'Weekly Reports' : undefined}
            >
              <FileDown size={18} className="flex-shrink-0" />
              {!isSidebarCollapsed && <span>Weekly Reports</span>}
            </NavLink>

            {isAdmin && (
              <NavLink
                to="/settings"
                onClick={() => {
                  if (window.innerWidth < 1024) setSidebarOpen(false);
                }}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200
                  ${
                    isActive
                      ? 'bg-[#C08B5C] text-white shadow-md shadow-[#C08B5C]/20'
                      : 'text-[#9CA3AF] hover:bg-[#2C3A50] hover:text-white'
                  }
                  ${isSidebarCollapsed ? 'justify-center' : ''}`
                }
                title={isSidebarCollapsed ? 'Settings' : undefined}
              >
                <Settings size={18} className="flex-shrink-0" />
                {!isSidebarCollapsed && <span>Settings</span>}
              </NavLink>
            )}
          </div>
        </nav>

        {/* User card at bottom */}
        <div className="border-t border-[#2C3A50] p-3">
          {isSidebarCollapsed ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#2C3A50] flex items-center justify-center text-xs font-semibold text-[#D4A574]">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <button
                onClick={logout}
                className="p-1.5 rounded-md hover:bg-[#2C3A50] text-[#6B7280] hover:text-red-400 transition-colors"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-[#2C3A50] flex items-center justify-center text-sm font-semibold text-[#D4A574] flex-shrink-0">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-white truncate">
                  {user?.name || 'User'}
                </div>
                <span
                  className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${roleBadgeStyles}`}
                >
                  {user?.role || 'external'}
                </span>
              </div>
              <button
                onClick={logout}
                className="p-1.5 rounded-md hover:bg-[#2C3A50] text-[#6B7280] hover:text-red-400 transition-colors flex-shrink-0"
                title="Sign out"
              >
                <LogOut size={16} />
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
